import type { Product } from "../lib/mockData";
import { useEffect, useMemo, useState } from 'react';
import { resolvePickedVariantSelection } from "../lib/utils";

interface ProductCardProps {
  product: Product;
  onProductSelect?: (product: Product) => void;
  fullWidth?: boolean;
  size?: 'default' | 'small';
}

export function ProductCard({
  product,
  onProductSelect,
  fullWidth = false,
  size = 'default',
}: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const appendCurrencyDisplay = (value?: string) => {
    const amount = (value || "").trim();
    const currencyDisplay = (product.currencyDisplay || "").trim();
    if (!amount || !currencyDisplay) return amount;
    if (amount.toLowerCase().endsWith(currencyDisplay.toLowerCase())) return amount;
    return `${amount} ${currencyDisplay}`;
  };

  // Ordered list of image URLs the card may show, best-first. The card walks
  // this list when an image fails to load (see onError below) so a single dead
  // CDN url doesn't blank the whole card. The list is constrained so we never
  // show an image belonging to a different color variant than the picked one.
  const cardImageCandidates = useMemo<string[]>(() => {
    const allImages = Array.isArray(product.images)
      ? product.images.filter((u): u is string => typeof u === "string" && u.length > 0)
      : [];
    if (allImages.length === 0) return [];

    const dims = product.variantDimensions;
    const combos = product.variantCombinations;
    const meta = product.imageMeta;

    // Without variant data we can't attribute images to a color, so trying any
    // of the product's images is acceptable.
    if (!Array.isArray(dims) || dims.length === 0) return allImages;
    if (!Array.isArray(combos) || combos.length === 0) return allImages;

    const resolved = resolvePickedVariantSelection(dims, combos, product.pickedVariantValues);
    if (!resolved || resolved.length !== dims.length) return allImages;

    const combo = combos.find((c) => Array.isArray(c?.values) && c.values.every((v, i) => v === resolved[i]));
    const imageIds = Array.isArray(combo?.image_ids)
      ? combo.image_ids.filter((id): id is string => typeof id === "string" && id.length > 0)
      : [];

    // No color-specific image list available: can't isolate the color, so any
    // product image is acceptable.
    if (imageIds.length === 0) return allImages;

    // A color is selected and has an explicit image list. Restrict strictly to
    // those images so we never display another color variant. If the ids can't
    // be mapped to urls (missing imageMeta), show nothing rather than risk a
    // wrong-color image.
    if (!Array.isArray(meta) || meta.length === 0) return [];

    const idSet = new Set(imageIds);
    const seen = new Set<string>();
    const colorImages: string[] = [];
    for (const img of meta) {
      if (!idSet.has(img.id)) continue;
      const url = img.url;
      if (typeof url !== "string" || url.length === 0 || seen.has(url)) continue;
      seen.add(url);
      colorImages.push(url);
    }
    return colorImages;
  }, [
    product.images,
    product.variantDimensions,
    product.variantCombinations,
    product.pickedVariantValues,
    product.imageMeta,
  ]);

  // Reset to the best candidate whenever the candidate list changes.
  useEffect(() => {
    setImageIndex(0);
  }, [cardImageCandidates]);

  const cardImageSrc = cardImageCandidates[imageIndex] ?? "";

  const bodyTypeClass = size === 'small' ? 'ww-type-body-sm' : 'ww-type-body-md';
  
  const handleClick = () => {
    if (onProductSelect) {
      onProductSelect(product);
    }
  };
  const showImage = Boolean(cardImageSrc);
  
  return (
    <div
      className={`h-full rounded-lg overflow-hidden w-full ${fullWidth ? '' : 'max-w-[300px]'} cursor-pointer`}
      style={{
        transition: 'border-color 0.3s ease',
        border: isHovered ? 'var(--product-card-border-hover)' : 'var(--product-card-border)',
        background: 'var(--color-background)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        className="relative"
        style={{ aspectRatio: 'var(--product-card-image-aspect, 1 / 1)' }}
      >
        {showImage ? (
          <img
            key={cardImageSrc}
            src={cardImageSrc}
            alt={product.title}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageIndex((i) => i + 1)}
          />
        ) : (
          <div className="w-full h-full bg-gray-200" aria-hidden="true" />
        )}
      </div>
      <div className="p-6 items-start">
        <h3
          className={`${bodyTypeClass} ww-type-label mb-1 text-left`}
        >
          {product.title}
        </h3>
        <p
          className={`${bodyTypeClass} mb-4 text-left`}
        >
          {product.compareAtPrice && product.compareAtPrice !== product.price && (
            <span
              className={`block line-through ${bodyTypeClass}`}
            >
              {appendCurrencyDisplay(product.compareAtPrice)}
            </span>
          )}
          <span className={`block ${bodyTypeClass}`}>{appendCurrencyDisplay(product.price)}</span>
        </p>
      </div>
    </div>
  );
} 